import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;




const DesktopContainer = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileContainer = styled.div`
  @media (min-width: 769px) {
    display: none;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  color: #333;
  font-weight: 500;
  border-bottom: 1px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CompanyName = styled.div`
  font-weight: 500;
  color: #333;
`;

const BusinessActivity = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Email = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const OwnerName = styled.div`
  font-weight: 500;
  color: #333;
`;

const OwnersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const OwnerItem = styled.div`
  font-size: 0.9rem;
  color: #333;
  padding: 0.25rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const OwnerShare = styled.span`
  color: #667eea;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'draft':
        return 'background: #e9ecef; color: #495057;';
      case 'submitted':
        return 'background: #d1ecf1; color: #0c5460;';
      case 'awaiting_payment':
        return 'background: #fff3cd; color: #856404;';
      case 'payment_verification':
        return 'background: #f8d7da; color: #721c24;';
      case 'in_progress':
        return 'background: #cce5ff; color: #004085;';
      case 'awaiting_documents':
        return 'background: #e2e3e5; color: #383d41;';
      case 'awaiting_signatures':
        return 'background: #d4edda; color: #155724;';
      case 'completed':
        return 'background: #d4edda; color: #155724;';
      case 'rejected':
        return 'background: #f8d7da; color: #721c24;';
      default:
        return 'background: #e9ecef; color: #495057;';
    }
  }}
`;

const ViewLink = styled(Link)`
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const RegistrationCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
`;

const CardId = styled.div`
  font-weight: 500;
  color: #333;
`;

const CardBody = styled.div`
  padding: 1rem;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CardLabel = styled.div`
  font-weight: 500;
  color: #555;
  min-width: 120px;
`;

const CardValue = styled.div`
  flex: 1;
  text-align: right;
  color: #333;
`;

const CardActions = styled.div`
  padding: 1rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  margin-left: 0.5rem;
  transition: all 0.2s;
  
  ${props => props.variant === 'danger' ? `
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  ` : `
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a6fd8;
    }
  `}
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ModalTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const ModalMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const DeleteConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: #dc3545;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #c82333;
  }
`;

const PaginationSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageSizeLabel = styled.label`
  color: #666;
  font-size: 0.9rem;
`;

const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button<{ disabled?: boolean; active?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#667eea' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#5a6fd8' : '#f8f9fa'};
    border-color: #667eea;
  }
  
  &:disabled {
    opacity: 0.5;
    background: #f8f9fa;
  }
`;

const PaginationEllipsis = styled.span`
  padding: 0.5rem;
  color: #666;
  font-size: 0.9rem;
`;

const ControlsSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ControlLabel = styled.label`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
`;

const ControlSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SortOrderSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

interface Registration {
  id: number;
  user_email: string;
  status: string;
  company_name_armenian: string;
  business_activity: string;
  owners_data: string;
  authorized_capital: number;
  created_at: string;
  updated_at: string;
}

interface Owner {
  full_name: string;
  address: string;
  passport_scan_path?: string;
  additional_proof_path?: string;
  share_percentage: number;
}

const AdminRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; registrationId: number | null; registrationName: string }>({
    show: false,
    registrationId: null,
    registrationName: ''
  });

  const fetchRegistrations = useCallback(async () => {
    try {
      setIsLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const params: any = {
        skip,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      console.log('Fetching registrations with params:', params, 'currentPage:', currentPage, 'pageSize:', pageSize, 'sortBy:', sortBy, 'sortOrder:', sortOrder);
      const response = await axios.get(getCurrentApiEndpoints().ADMIN_REGISTRATIONS, { params });
      console.log('Received registrations:', response.data.length, 'registrations');
      setRegistrations(response.data);
      
      // Extract total count from response headers or response data
      const total = response.headers['x-total-count'] || response.data.length;
      setTotalCount(parseInt(total));
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, currentPage, pageSize, sortBy, sortOrder]);

  const handleDelete = async (registrationId: number) => {
    try {
      await axios.delete(getCurrentApiEndpoints().DELETE_REGISTRATION(registrationId));
      // Remove the deleted registration from the list
      setRegistrations(prev => prev.filter(reg => reg.id !== registrationId));
      setDeleteModal({ show: false, registrationId: null, registrationName: '' });
    } catch (error) {
      console.error('Failed to delete registration:', error);
      alert('Failed to delete registration. Please try again.');
    }
  };

  const showDeleteModal = (registration: Registration) => {
    const companyName = registration.company_name_armenian || registration.business_activity || `Registration #${registration.id}`;
    setDeleteModal({
      show: true,
      registrationId: registration.id,
      registrationName: companyName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, registrationId: null, registrationName: '' });
  };

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]); // Re-fetch when fetchRegistrations changes

  const handleFilterChange = (newStatusFilter: string) => {
    setStatusFilter(newStatusFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleSortChange = (newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'payment_verification': return 'Payment Verification';
      case 'in_progress': return 'In Progress';
      case 'awaiting_documents': return 'Awaiting Documents';
      case 'awaiting_signatures': return 'Awaiting Signatures';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const parseOwners = (ownersData: string | null | undefined): Owner[] => {
    try {
      return ownersData ? JSON.parse(ownersData) : [];
    } catch {
      return [];
    }
  };

  const getStats = () => {
    const total = registrations.length;
    const draft = registrations.filter(r => r.status === 'draft').length;
    const submitted = registrations.filter(r => r.status === 'submitted').length;
    const awaitingPayment = registrations.filter(r => r.status === 'awaiting_payment').length;
    const paymentVerification = registrations.filter(r => r.status === 'payment_verification').length;
    const inProgress = registrations.filter(r => r.status === 'in_progress').length;
    const awaitingDocuments = registrations.filter(r => r.status === 'awaiting_documents').length;
    const awaitingSignatures = registrations.filter(r => r.status === 'awaiting_signatures').length;
    const completed = registrations.filter(r => r.status === 'completed').length;
    const rejected = registrations.filter(r => r.status === 'rejected').length;
    
    return { 
      total, 
      draft, 
      submitted, 
      awaitingPayment, 
      paymentVerification, 
      inProgress, 
      awaitingDocuments, 
      awaitingSignatures, 
      completed, 
      rejected 
    };
  };

  const getPaginationInfo = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);
    
    return {
      totalPages,
      startItem,
      endItem,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    };
  };

  const getPageNumbers = () => {
    const { totalPages } = getPaginationInfo();
    const pages: (number | string)[] = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 4) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };


  return (
    <Container>
      <Header>
        <Title>Registration Management</Title>
      </Header>

      

      {/* Controls Section - Merged Filter and Sort */}
      <ControlsSection>
        <ControlGroup>
          <ControlLabel htmlFor="status-filter">Filter:</ControlLabel>
          <ControlSelect
            id="status-filter"
            value={statusFilter}
            onChange={(e) => {
              handleFilterChange(e.target.value);
            }}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="awaiting_payment">Awaiting Payment</option>
            <option value="payment_verification">Payment Verification</option>
            <option value="in_progress">In Progress</option>
            <option value="awaiting_documents">Awaiting Documents</option>
            <option value="awaiting_signatures">Awaiting Signatures</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </ControlSelect>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel htmlFor="sort-by">Sort by:</ControlLabel>
          <SortSelect
            id="sort-by"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value, sortOrder)}
          >
            <option value="created_at">Created Date</option>
            <option value="updated_at">Updated Date</option>
            <option value="status">Status</option>
            <option value="company_name_armenian">Company Name</option>
            <option value="user_email">User Email</option>
            <option value="authorized_capital">Authorized Capital</option>
          </SortSelect>
          
          <SortOrderSelect
            value={sortOrder}
            onChange={(e) => handleSortChange(sortBy, e.target.value)}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </SortOrderSelect>
        </ControlGroup>
      </ControlsSection>

      {/* Desktop Table View */}
      <DesktopContainer>
        <TableContainer>
          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading registrations...</div>
          ) : registrations.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>No registrations found</div>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Company</TableHeader>
                    <TableHeader>User</TableHeader>
                    {/* <TableHeader>Owners</TableHeader> */}
                    {/* <TableHeader>Capital</TableHeader> */}
                    <TableHeader>Created</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((registration) => {
                    const owners = parseOwners(registration.owners_data);
                    return (
                      <tr key={registration.id}>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>#{registration.id}</span>
                            <StatusBadge status={registration.status}>
                              {getStatusLabel(registration.status)}
                            </StatusBadge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <CompanyInfo>
                            <CompanyName>
                              {registration.company_name_armenian || 'N/A'}
                            </CompanyName>
                            <BusinessActivity>
                            </BusinessActivity>
                          </CompanyInfo>
                        </TableCell>
                        <TableCell>
                          <UserInfo>
                            <Email className="user-email">
                              {registration.user_email}
                            </Email>
                          </UserInfo>
                        </TableCell>
                        {/* <TableCell>
                          <OwnersList>
                            {owners.length > 0 ? (
                              owners.map((owner, index) => (
                                <OwnerItem key={index}>
                                  <div>{owner.full_name}</div>
                                  <OwnerShare>{owner.share_percentage}%</OwnerShare>
                                </OwnerItem>
                              ))
                            ) : (
                              <OwnerItem>No owners specified</OwnerItem>
                            )}
                          </OwnersList>
                        </TableCell> */}
                        {/* <TableCell>
                          {registration.authorized_capital 
                            ? registration.authorized_capital.toLocaleString() 
                            : 'N/A'}
                        </TableCell> */}
                        <TableCell>{formatDate(registration.created_at)}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <ViewLink to={`/admin/registrations/${registration.id}`}>
                              View Details
                            </ViewLink>
                            <ActionButton 
                              variant="danger" 
                              onClick={() => showDeleteModal(registration)}
                            >
                              Delete
                            </ActionButton>
                          </div>
                        </TableCell>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </TableContainer>
      </DesktopContainer>

      {/* Mobile Card View */}
      <MobileContainer>
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading registrations...</div>
        ) : registrations.length === 0 ? (
          <RegistrationCard>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              No registrations found
            </div>
          </RegistrationCard>
        ) : (
          registrations.map((registration) => {
            const owners = parseOwners(registration.owners_data);
            return (
              <RegistrationCard key={registration.id}>
                <CardHeader>
                  <CardId>#{registration.id}</CardId>
                  <StatusBadge status={registration.status}>
                    {getStatusLabel(registration.status)}
                  </StatusBadge>
                </CardHeader>
                
                <CardBody>
                  <CardRow>
                    <CardLabel>Company Name</CardLabel>
                    <CardValue>{registration.company_name_armenian || 'N/A'}</CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Business Activity</CardLabel>
                    <CardValue>{registration.business_activity || 'N/A'}</CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>User Email</CardLabel>
                    <CardValue>{registration.user_email}</CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Owners</CardLabel>
                    <CardValue>
                      {owners.length > 0 ? (
                        <div style={{ textAlign: 'left' }}>
                          {owners.map((owner, index) => (
                            <div key={index} style={{ marginBottom: '0.5rem' }}>
                              <div>{owner.full_name}</div>
                              <div style={{ color: '#667eea', fontSize: '0.9rem' }}>
                                {owner.share_percentage}% share
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        'No owners specified'
                      )}
                    </CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Authorized Capital</CardLabel>
                    <CardValue>
                      {registration.authorized_capital 
                        ? `${registration.authorized_capital.toLocaleString()} AMD`
                        : 'N/A'}
                    </CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Created</CardLabel>
                    <CardValue>{formatDate(registration.created_at)}</CardValue>
                  </CardRow>
                </CardBody>
                
                <CardActions>
                  <ViewLink to={`/admin/registrations/${registration.id}`}>
                    View Details →
                  </ViewLink>
                  <ActionButton 
                    variant="danger" 
                    onClick={() => showDeleteModal(registration)}
                  >
                    Delete
                  </ActionButton>
                </CardActions>
              </RegistrationCard>
            );
          })
        )}
      </MobileContainer>

      {/* Pagination Section - Moved to Bottom */}
      <PaginationSection>
        <PaginationInfo>
          <div>
            Showing {getPaginationInfo().startItem}-{getPaginationInfo().endItem} of {totalCount} registrations
          </div>
          <PageSizeSelector>
            <PageSizeLabel htmlFor="page-size">Items per page:</PageSizeLabel>
            <PageSizeSelect
              id="page-size"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </PageSizeSelect>
          </PageSizeSelector>
        </PaginationInfo>
        
        <PaginationControls>
          <PaginationButton
            disabled={!getPaginationInfo().hasPrevPage}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </PaginationButton>
          
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <PaginationEllipsis key={`ellipsis-${index}`}>...</PaginationEllipsis>
            ) : (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page as number)}
              >
                {page}
              </PaginationButton>
            )
          ))}
          
          <PaginationButton
            disabled={!getPaginationInfo().hasNextPage}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </PaginationButton>
        </PaginationControls>
      </PaginationSection>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <Modal>
          <ModalContent>
            <ModalTitle>Confirm Deletion</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete the registration for "{deleteModal.registrationName}"?
              This action cannot be undone.
            </ModalMessage>
            <ModalButtons>
              <CancelButton onClick={closeDeleteModal}>Cancel</CancelButton>
              <DeleteConfirmButton onClick={() => handleDelete(deleteModal.registrationId!)}>
                Delete
              </DeleteConfirmButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminRegistrations;